import { motion } from 'framer-motion';
import { FileText, Scale, Handshake, AlertTriangle } from 'lucide-react';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/20 rounded-full mb-6">
              <FileText className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Terms of Service
            </h1>
            <p className="text-slate-400">
              Last Updated: February 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="prose prose-lg prose-invert max-w-none"
          >
            <div className="bg-slate-800 rounded-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <Scale className="w-6 h-6 text-emerald-400" />
                Agreement to Terms
              </h2>
              <p className="text-slate-300">
                By accessing or using the Cornerstone Refund website and services, you agree to be bound by 
                these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </div>

            <div className="space-y-8 text-slate-300">
              <section>
                <h3 className="text-xl font-semibold text-white mb-4">1. Services Description</h3>
                <p className="mb-4">
                  Cornerstone Refund provides overage fund recovery services for former property owners in 
                  California and Arizona. Our services include:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Researching and identifying potential overage funds from foreclosure sales</li>
                  <li>Preparing and filing necessary documentation with county offices</li>
                  <li>Communicating with relevant authorities on your behalf</li>
                  <li>Processing and facilitating the recovery of surplus funds</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">2. Fee Structure</h3>
                <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-6 mb-4">
                  <div className="flex items-start gap-3">
                    <Handshake className="w-6 h-6 text-emerald-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-semibold mb-2">Contingency Fee Basis</p>
                      <p className="text-slate-300">
                        We operate on a contingency fee basis. This means you pay nothing upfront, and our 
                        fee is a pre-agreed percentage of the funds we successfully recover for you. If we 
                        do not recover any funds, you owe us nothing.
                      </p>
                    </div>
                  </div>
                </div>
                <p className="mb-4">
                  The specific fee percentage will be clearly stated in the service agreement you sign 
                  before we begin work on your case. There are no hidden fees or additional charges for 
                  our services.
                </p>
                <p>
                  Minor out-of-pocket costs (such as notarization fees required by certain counties) may 
                  apply and will be communicated to you in advance.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">3. Client Responsibilities</h3>
                <p className="mb-4">As a client, you agree to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Provide accurate and complete information about yourself and your property</li>
                  <li>Sign and return required documents in a timely manner</li>
                  <li>Notify us of any changes to your contact information</li>
                  <li>Cooperate with reasonable requests for information needed to process your claim</li>
                  <li>Not engage other parties to pursue the same overage funds during our engagement</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">4. No Guarantee of Results</h3>
                <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-6 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-semibold mb-2">Important Notice</p>
                      <p className="text-slate-300">
                        While we make every effort to recover overage funds on your behalf, we cannot 
                        guarantee any specific outcome. The existence and amount of overage funds depend 
                        on various factors, including the sale price of the property and existing liens.
                      </p>
                    </div>
                  </div>
                </div>
                <p>
                  Each case is unique, and results vary based on individual circumstances. Our initial 
                  consultation will help us assess the potential for recovery in your specific situation.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">5. Termination</h3>
                <p className="mb-4">
                  Either party may terminate the service agreement at any time with written notice. Upon 
                  termination:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>If funds have already been recovered, the agreed-upon fee applies</li>
                  <li>If no funds have been recovered, no fee is owed</li>
                  <li>We will return any original documents provided by you</li>
                  <li>We may retain copies for our records as required by law</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">6. Limitation of Liability</h3>
                <p>
                  To the fullest extent permitted by law, Cornerstone Refund shall not be liable for any 
                  indirect, incidental, special, consequential, or punitive damages arising out of or 
                  related to our services. Our total liability shall not exceed the fees paid to us for 
                  the services in question.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">7. Intellectual Property</h3>
                <p>
                  All content on our website, including text, graphics, logos, and images, is the property 
                  of Cornerstone Refund and is protected by copyright and other intellectual property laws. 
                  You may not reproduce, distribute, or use our content without prior written permission.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">8. Website Use</h3>
                <p className="mb-4">When using our website, you agree not to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Use the website for any unlawful purpose</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with the proper functioning of the website</li>
                  <li>Submit false or misleading information</li>
                  <li>Use automated systems to access or scrape the website</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">9. Governing Law</h3>
                <p>
                  These Terms of Service shall be governed by and construed in accordance with the laws of 
                  the State of Arizona, without regard to its conflict of law provisions. Any disputes 
                  arising from these terms shall be resolved in the courts of Maricopa County, Arizona.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">10. Changes to Terms</h3>
                <p>
                  We reserve the right to modify these Terms of Service at any time. Changes will be 
                  effective immediately upon posting to our website. Your continued use of our services 
                  after any changes constitutes acceptance of the new terms.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">11. Severability</h3>
                <p>
                  If any provision of these Terms of Service is found to be unenforceable, the remaining 
                  provisions will continue in full force and effect.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-4">12. Contact Information</h3>
                <p className="mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="mb-2"><strong className="text-white">Cornerstone Refund</strong></p>
                  <p>2345 E. Thomas Rd Ste 100 #510</p>
                  <p>Phoenix, AZ 85016</p>
                  <p>Phone: <a href="tel:405-754-2183" className="text-emerald-400 hover:underline">405-754-2183</a></p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
